import { useLocation } from "react-router-dom";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import logo from "../assets/images/healthmatch-logo-primary.svg";
import { LogIn } from "lucide-react";
import { LandingPageFooter } from "./LandingPageFooter";
import { useScrollPosition } from "../hooks/useScrollPosition";
import { useScrollToTop } from "../hooks/useScrollManagement";
import { Helmet } from "react-helmet-async";

function PageTitle() {
  const location = useLocation();

  const getTitle = () => {
    // Landing page routes will set their own titles
    if (location.pathname !== "/") {
      return "";
    }

    return "HealthMatch - Connecting Patients to Clinical Trials";
  };

  const title = getTitle();
  return title ? (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  ) : null;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isScrolled = useScrollPosition();
  const location = useLocation();

  useScrollToTop([location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav
        className={clsx(
          "fixed top-0 left-0 right-0 z-50 bg-white",
          isScrolled ? "shadow-lg" : "shadow-sm",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="h-5">
                <img
                  src={logo}
                  alt="HealthMatch"
                  className="h-full w-auto transition-all duration-300 logo-colored"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="https://app.healthmatch.io/login"
                className="inline-flex items-center px-4 py-2 border-transparent text-white bg-primary-500 hover:bg-primary-600 text-sm font-medium rounded-md transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Patient Login
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-100"
              >
                <span className="sr-only">Open menu</span>
                {isMobileMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="space-y-1">
                <div className="px-4 py-2">
                  <a
                    href="https://app.healthmatch.io/login"
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-primary-500 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="flex items-center">
                      <LogIn className="w-4 h-4 mr-2" />
                      Patient Login
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      <PageTitle />
      {children}

      <LandingPageFooter />
    </div>
  );
}
